import type { Key, ReactNode } from 'react';
import { Button, Flex } from 'antd';
import { CopyOutlined, PlusOutlined } from '@ant-design/icons';
import { t } from 'i18next';

import { CrudMode, FormItem } from '@ohpen/enso-ui';
import { ActionEntityType } from '@pmp/api';
import { DEFAULT_TEXT_MAX_LENGTH_SHORT, DEFAULT_TEXTAREA_MAX_LENGTH, HALF_WIDTH, crudSection, textLimitProps } from '@pmp/utils';

const enableCopyViewsByEntity = new Set<ActionEntityType>([ActionEntityType.PRODUCTS]);

export type GetFormSchemaParams = {
  mode: CrudMode | null | undefined;
  isReadOnly: boolean;
  entityType: ActionEntityType | null | undefined;
  required: { required: boolean; message: string }[];
  getRow: (key: Key) => ReactNode;
  onDragEnd: (order: Key[]) => void;
  onCopyView: () => void;
  onAddColumn: () => void;
};

export const getFormSchema = ({
  mode,
  isReadOnly,
  entityType,
  required,
  getRow,
  onDragEnd,
  onCopyView,
  onAddColumn,
}: GetFormSchemaParams): FormItem[] => {
  const enableCopyViews = !!entityType && enableCopyViewsByEntity.has(entityType);

  return [
    crudSection('global.labels.general', mode, [
      {
        dataIndex: 'entityType',
        title: 'global.labels.entityType',
        colProps: HALF_WIDTH,
        fieldProps: { disabled: true },
      },
      {
        dataIndex: 'id',
        title: 'global.labels.id',
        colProps: HALF_WIDTH,
        formItemProps: { hidden: mode !== CrudMode.EDIT },
        fieldProps: { disabled: true },
      },
      {
        dataIndex: 'name',
        title: 'global.labels.label',
        formItemProps: { rules: required },
        fieldProps: { disabled: isReadOnly, ...textLimitProps(DEFAULT_TEXT_MAX_LENGTH_SHORT) },
      },
      {
        dataIndex: 'description',
        title: 'global.labels.description',
        valueType: 'textarea',
        formItemProps: { rules: required },
        fieldProps: { disabled: isReadOnly, ...textLimitProps(DEFAULT_TEXTAREA_MAX_LENGTH) },
      },
    ]),
    crudSection('global.labels.exportTemplateMapping', mode, [
      {
        valueType: 'alert',
        fieldProps: { message: t('global.labels.exportTemplateMappingMessage'), type: 'info' },
      },
      {
        dataIndex: 'columnOrder',
        valueType: 'sortableList',
        sortableListProps: {
          renderItem: (key: Key) => getRow(key),
          onDragEnd,
          header: (
            <Flex justify="end">
              {enableCopyViews && (
                <Button size="small" icon={<CopyOutlined />} onClick={onCopyView} disabled={isReadOnly}>
                  {t('global.buttons.copyView')}
                </Button>
              )}
            </Flex>
          ),
          footer: (
            <Flex justify="end">
              <Button size="small" icon={<PlusOutlined />} onClick={onAddColumn} disabled={isReadOnly}>
                {t('global.labels.addColumn')}
              </Button>
            </Flex>
          ),
        },
      },
    ]),
  ];
};
